import React, { useEffect, useMemo, useState } from "react";
import {
  Form,
  Button,
  FormField,
  ColumnLayout,
  Container,
  ExpandableSection,
  Textarea,
  Alert,
  Popover,
  Box,
  StatusIndicator,
  Toggle,
  SpaceBetween,
} from "@amzn/awsui-components-react/polaris";
import { useDispatch, useSelector } from "react-redux";
import {
  resetFilter,
  setQueryString,
  setPendingQueryString,
  setQueryMode,
  setAttributeQuery,
  unsetAttributeQuery,
  Range,
} from "./searchSideBarConnector";
import { RootState } from "../app/rootReducer";
import {
  LAUNCH_ASIN_TYPE_MULTI_SELECT_ITEMS,
  ASIN_PARTY_SELECT_ITEMS,
  MARKETPLACE_MULTI_SELECT_ITEMS,
  toMultiSelectOptionsWithIndexCount,
  toMultiSelectOption,
  VALIDATION_STATUS_SELECT_ITEMS,
  VALIDATION_STATUS_ITEMS_FAIL,
  VALIDATION_STATUS_ITEMS_PASS,
  BASELINE_COMPLIANCE_STATUS_MULTI_SELECT_ITEMS,
  BASELINE_STATUS_ITEMS_COMPLIANT,
  BASELINE_STATUS_ITEMS_NON_COMPLIANT,
  VALIDATION_STATUS_ITEMS_PASS_EXCEPTION,
  WEBLAB_STATUS_SELECT_ITEMS,
  BUYBOX_STATUS_SELECT_ITEMS,
} from "./SearchSideBar-config";
import { LAUNCH_ASIN_TYPES_EXTENDED } from "../../constants/launchAsinTypes";
import {
  StringField,
  MultiselectField,
  DateRangeField,
} from "./searchComponents";
import { getProgramNames } from "./upstreamAttributesConnector";
import "./SearchSideBar.css";
import { getProductLines } from "./productLinesConnector";
import { setAsinPageNumber } from "../porAsinPageControl/porAsinPageControl";
import { setValidationPageNumber } from "../validation/asinValidationTableConnector";
import { getAmazonTinyUrl } from "../app/api";
import { trackButtonClick } from "../telemetry/telemetry";
import { setBaselinePageNumber } from "../dateBaseline/baselineTableConnector";
import { CopyToClipboard } from "../../util/browserUtils";
import BaselineIdTokenGroup from "./BaselineIdTokenGroup";
import SearchPropertyFilter from "./searchPropertyFilter/searchPropertyFilter";
import { getFilterWithAggregatedData } from "./filterWithAggregatedDataConnector";

const UnrepresentableQuery = (error?: string) => {
  if (error !== undefined && error !== "") {
    return (
      <div>
        <Alert visible={true} header="Search Query Issue:" type="error">
          <p>{error}.</p>
          Please review and adjust your search criteria to match the expected
          format.
        </Alert>
      </div>
    );
  }
  return (
    <div>
      <Alert visible={true} header="Unrepresentable Query"></Alert>
      <p>
        This query string is not representable in the Devices ASIN Central UI
        search panel <i>but was still sent to the server</i>. Results in the
        ASINs table reflect this query.
      </p>
      <p>
        You can edit the query directly below to view different results or reset
        the filter to start from scratch with the search panel UI.
      </p>
    </div>
  );
};

const OnCopy = () => {
  getAmazonTinyUrl(window.location.href).then((tinyUrl) => {
    trackButtonClick("searchSideBar", "copyUrl");
    CopyToClipboard(tinyUrl);
  });
};

export const CopyButton = () => {
  const dispatch = useDispatch();
  return (
    <div data-testid="copy-button-wrapper">
      <Box float="left">
        <Popover
          size="small"
          position="top"
          triggerType="custom"
          dismissButton={false}
          content={
            <StatusIndicator type="success">Link copied</StatusIndicator>
          }
        >
          <Button
            data-testid="copy-url-button"
            iconName={"copy"}
            children={"Copy"}
            variant={"icon"}
            onClick={() => {
              trackButtonClick("searchSideBar", "copy", {});
              dispatch(OnCopy);
            }}
          />
        </Popover>
      </Box>
    </div>
  );
};

export const SearchSideBar = () => {
  const dispatch = useDispatch();
  const {
    asin,
    country,
    programId,
    productLineId,
    asinParty,
    asinType,
    announceDate,
    onlineStreetDate,
    validationStatus,
    baselineStatus,
    baselineId,
    makoWeblabStatus,
    buyBoxStatus,
  } = useSelector((state: RootState) => state.generateQuery.queryParameters);
  const {
    unrepresentable,
    queryString,
    pendingQueryString,
    queryParseError,
    queryMode,
  } = useSelector((state: RootState) => state.generateQuery);

  const { filterWithAggregatedData } = useSelector(
    (state: RootState) => state.filterWithAggregatedData
  );

  const { programNameData } = useSelector(
    (state: RootState) => state.upstreamAttributesData
  );

  const [programNameDataStatus, setProgramNameDataStatus] = useState<
    "pending" | "loading" | "finished" | "error"
  >("pending");

  const { productLinesData } = useSelector(
    (state: RootState) => state.productLines
  );

  const [productLinesDataStatus, setProductLinesDataStatus] = useState<
    "pending" | "loading" | "finished" | "error"
  >("pending");

  const { isEditing } = useSelector(
    (state: RootState) => state.asinBaselineTableControl
  );

  useEffect(() => {
    dispatch(getFilterWithAggregatedData());
  }, []);

  useEffect(() => {
    if (filterWithAggregatedData.status === "finished") {
      dispatch(getProgramNames());
      dispatch(getProductLines());
    } else {
      setProductLinesDataStatus(filterWithAggregatedData.status ?? "pending");
      setProgramNameDataStatus(filterWithAggregatedData.status ?? "pending");
    }
  }, [filterWithAggregatedData]);

  useEffect(() => {
    if (programNameData.status) {
      setProgramNameDataStatus(programNameData.status);
    }
  }, [programNameData]);

  useEffect(() => {
    if (productLinesData.status) {
      setProductLinesDataStatus(productLinesData.status);
    }
  }, [productLinesData]);

  const productLinesMemoizedOptions = useMemo(
    () =>
      toMultiSelectOptionsWithIndexCount(
        productLinesData.values.map((pl) =>
          toMultiSelectOption(pl.id, pl.name)
        ),
        filterWithAggregatedData.data?.productLineId || {}
      ),
    [productLinesData]
  );

  const programNameMemoizedOptions = useMemo(
    () =>
      toMultiSelectOptionsWithIndexCount(
        programNameData.values.map((program) =>
          toMultiSelectOption(program.id, program.name)
        ),
        filterWithAggregatedData.data?.programId || {}
      ),
    [programNameData]
  );

  const normalForm = () => (
    <ColumnLayout>
      <SpaceBetween size="s">
        <FormField id={"asin-form"} label="ASIN" stretch={true}>
          {StringField(
            "B004AT6YQN, B00CC8XHN9, B00CUTCNXM, B00KBNMT06 ...",
            "asin",
            asin?.value as string,
            dispatch
          )}
        </FormField>
        <FormField id={"marketplace-form"} label="Marketplace" stretch={true}>
          {MultiselectField(
            MARKETPLACE_MULTI_SELECT_ITEMS,
            "Choose Marketplaces",
            "country",
            country?.value as string,
            dispatch
          )}
        </FormField>
        <FormField id={"product-line-form"} label="Product Line" stretch={true}>
          {MultiselectField(
            productLinesMemoizedOptions ?? [],
            "Choose Product Lines",
            "productLineId",
            productLineId?.value as string,
            dispatch,
            productLinesDataStatus,
            "Loading ProductLines",
            getFilterWithAggregatedData,
            (productLineId: string) => {
              const productLine = productLinesData.values.find(
                (productLine) => productLine.id === productLineId
              );
              return productLine?.name || productLineId;
            },
            true
          )}
        </FormField>
        <FormField id={"program-form"} label="Program" stretch={true}>
          {MultiselectField(
            programNameMemoizedOptions ?? [],
            "Choose Program Names",
            "programId",
            programId?.value as string,
            dispatch,
            programNameDataStatus,
            "Loading Program Names",
            getFilterWithAggregatedData,
            (programId: string) => {
              const program = programNameData.values.find(
                (program) => program.id === programId
              );
              return program?.name || programId;
            },
            true
          )}
        </FormField>
        <FormField id={"asin-type-form"} label="ASIN Type" stretch={true}>
          {MultiselectField(
            LAUNCH_ASIN_TYPE_MULTI_SELECT_ITEMS,
            "Choose ASIN Types",
            "asinType",
            asinType?.value as string,
            dispatch,
            undefined,
            undefined,
            undefined,
            (serviceName: string) =>
              LAUNCH_ASIN_TYPES_EXTENDED[serviceName] || serviceName
          )}
        </FormField>
        <FormField id={"asin-party-form"} label="ASIN Partiness" stretch={true}>
          {MultiselectField(
            ASIN_PARTY_SELECT_ITEMS,
            "Choose ASIN Parties",
            "asinParty",
            asinParty?.value as string,
            dispatch
          )}
        </FormField>
        <FormField
          id={"announce-date-form"}
          label="Announce Date"
          stretch={true}
        >
          <DateRangeField
            value={announceDate?.value as Range}
            onChange={(range) =>
              range
                ? dispatch(
                    setAttributeQuery({
                      attribute: "announceDate",
                      queryState: { value: range },
                    })
                  )
                : dispatch(unsetAttributeQuery({ attribute: "announceDate" }))
            }
          />
        </FormField>
        <FormField
          id={"street-date-form"}
          label="Online Street Date"
          stretch={true}
        >
          <DateRangeField
            value={onlineStreetDate?.value as Range}
            onChange={(range) =>
              range
                ? dispatch(
                    setAttributeQuery({
                      attribute: "onlineStreetDate",
                      queryState: { value: range },
                    })
                  )
                : dispatch(
                    unsetAttributeQuery({ attribute: "onlineStreetDate" })
                  )
            }
          />
        </FormField>
        {
          <FormField
            id={"validation-status-form"}
            label="Validation Status"
            stretch={true}
          >
            {MultiselectField(
              VALIDATION_STATUS_SELECT_ITEMS,
              "Choose Validation",
              "validationStatus",
              validationStatus?.value as string,
              dispatch,
              undefined,
              undefined,
              undefined,
              (validationStatus: string) =>
                VALIDATION_STATUS_ITEMS_PASS[validationStatus] ||
                VALIDATION_STATUS_ITEMS_FAIL[validationStatus] ||
                VALIDATION_STATUS_ITEMS_PASS_EXCEPTION[validationStatus] ||
                validationStatus
            )}
          </FormField>
        }
        {
          <FormField
            id={"baseline-status-form"}
            label="Baseline Status"
            stretch={true}
          >
            {MultiselectField(
              BASELINE_COMPLIANCE_STATUS_MULTI_SELECT_ITEMS,
              "Choose Baseline Status",
              "baselineStatus",
              baselineStatus?.value as string,
              dispatch,
              undefined,
              undefined,
              undefined,
              (baselineStatus: string) =>
                BASELINE_STATUS_ITEMS_COMPLIANT[baselineStatus] ||
                BASELINE_STATUS_ITEMS_NON_COMPLIANT[baselineStatus] ||
                baselineStatus
            )}
          </FormField>
        }
        {baselineId !== undefined && baselineId.value !== undefined && (
          <FormField
            id={"other-filters-form"}
            label="Other Filters Applied"
            stretch={true}
          >
            <BaselineIdTokenGroup />
          </FormField>
        )}
        <FormField
          id={"weblab-status-form"}
          label="Weblab Status"
          stretch={true}
        >
          {MultiselectField(
            WEBLAB_STATUS_SELECT_ITEMS,
            "Choose Weblab Status",
            "makoWeblabStatus",
            makoWeblabStatus?.value as string,
            dispatch
          )}
        </FormField>
        <FormField
          id={"buybox-status-form"}
          label="BuyBox Status"
          stretch={true}
        >
          {MultiselectField(
            BUYBOX_STATUS_SELECT_ITEMS,
            "Choose BuyBox Status",
            "buyBoxStatus",
            buyBoxStatus?.value as string,
            dispatch
          )}
        </FormField>
      </SpaceBetween>
    </ColumnLayout>
  );

  const propertySearchForm = () => (
    <ColumnLayout>
      <SpaceBetween size="s">
        <SearchPropertyFilter />
      </SpaceBetween>
    </ColumnLayout>
  );

  return (
    <div data-testid="search-sidebar-container" style={{ padding: "20px" }}>
      <Form
        data-testid="search-form"
        header={
          <div>
            <h2>Search</h2>
          </div>
        }
      >
        <FormField
          id={"query-mode-form"}
          label="Advanced Search"
          stretch={true}
        >
          <Toggle
            data-testid="query-mode-toggle"
            checked={queryMode === "PropertyFilter"}
            onChange={({ detail }) => {
              trackButtonClick("searchSideBar", "ToggleQueryMode", {
                queryMode: detail.checked ? "PropertyFilter" : "UIFilter",
              });
              dispatch(
                setQueryMode(detail.checked ? "PropertyFilter" : "UIFilter")
              );
              dispatch(resetFilter());
              dispatch(setQueryString());
            }}
          />
        </FormField>
        {unrepresentable
          ? UnrepresentableQuery(queryParseError)
          : queryMode === "PropertyFilter"
          ? propertySearchForm()
          : normalForm()}
        <ExpandableSection
          data-testid="query-string-section"
          header="Query String"
          variant="footer"
          defaultExpanded={unrepresentable}
        >
          <FormField
            id={"query-string-form"}
            label="Query string"
            description="Supply or modify the query string directly"
            stretch={true}
          >
            <Textarea
              data-testid="query-string-textarea"
              rows={10}
              onChange={({ detail }) =>
                dispatch(setPendingQueryString(detail.value))
              }
              value={pendingQueryString || queryString}
            />
          </FormField>
        </ExpandableSection>
        <br />
        <div data-testid="search-actions">
          <CopyButton />
          <Box float="right">
            <Button
              data-testid="reset-button"
              variant={"link"}
              children={"Reset"}
              onClick={() => {
                trackButtonClick("searchSideBar", "Reset", { queryMode });
                dispatch(resetFilter());
                dispatch(setQueryString());
              }}
            />
            <Button
              data-testid="submit-button"
              variant={"primary"}
              children={"Submit"}
              disabled={isEditing}
              onClick={() => {
                trackButtonClick("searchSideBar", "Submit", {
                  queryMode,
                  query: pendingQueryString,
                });
                dispatch(setQueryString());
                // Reset page number on fresh search. See LEAP-7748.
                dispatch(setAsinPageNumber({ pageNumber: 1 }));
                dispatch(setValidationPageNumber({ pageNumber: 1 }));
                dispatch(setBaselinePageNumber({ pageNumber: 1 }));
              }}
            />
          </Box>
        </div>
        <br />
      </Form>
    </div>
  );
};
